import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const ResourcesPage = () => {
  const resources = [
    {
      title: 'Insurance Guides',
      description: 'Comprehensive guides to understanding construction insurance coverage for Pennsylvania contractors',
      items: ['General Liability Insurance Explained', 'Workers Compensation Requirements in PA', 'Commercial Auto Coverage Guide', 'Equipment Insurance Basics', 'Umbrella Policy Benefits'],
      icon: 'ri-book-open-line',
    },
    {
      title: 'Bonding Resources',
      description: 'Essential information about surety bonds for Bucks County and Montgomery County contractors',
      items: ['Understanding Bid Bonds', 'Performance Bond Requirements in PA', 'Payment Bond Essentials', 'How to Qualify for Bonding', 'Increasing Your Bond Capacity'],
      icon: 'ri-file-shield-line',
    },
    {
      title: 'Risk Management',
      description: 'Tools and strategies to minimize construction risks across Pennsylvania',
      items: ['Safety Program Development', 'Claims Prevention Strategies', 'Contract Review Checklist', 'Subcontractor Management', 'Project Documentation Best Practices'],
      icon: 'ri-shield-check-line',
    },
    {
      title: 'Industry Updates',
      description: 'Stay informed about construction industry changes in Pennsylvania',
      items: ['Insurance Market Trends', 'Regulatory Changes in PA', 'New Coverage Options', 'Industry Best Practices', 'Technology in Construction'],
      icon: 'ri-newspaper-line',
    },
  ];

  const tools = [
    { title: 'Online Payment Portal', description: 'Make secure payments for your insurance premiums and bonds', link: 'https://payments.myappliedproducts.com/flow/hpp', icon: 'ri-secure-payment-line', external: true },
    { title: 'Quote Request', description: 'Get a customized construction insurance and bonding quote for your PA business', link: '/contact', icon: 'ri-file-list-line', external: false },
    { title: 'The Insurance Alliance', description: 'Learn more about our Pennsylvania alliance network', link: 'https://www.theinsurancealliancenetwork.com/', icon: 'ri-links-line', external: true },
  ];

  const faqs = [
    {
      question: 'What types of insurance do contractors in Bucks County PA need?',
      answer: 'Contractors in Bucks County and Montgomery County, PA typically need General Liability, Workers Compensation, Commercial Auto, Equipment Coverage, and Umbrella policies. The specific requirements depend on your operations and contract requirements.',
    },
    {
      question: 'How do I qualify for surety bonds in Pennsylvania?',
      answer: 'Qualification for surety bonds in Pennsylvania depends on your financial strength, experience, and track record. We work with you to prepare your financial statements and present your company in the best light to surety companies.',
    },
    {
      question: 'What is the difference between insurance and bonding?',
      answer: 'Insurance protects you from losses, while bonds guarantee your performance to project owners. Insurance is for your protection; bonds are for the protection of the project owner.',
    },
    {
      question: 'How long does it take to get bonded in Montgomery County PA?',
      answer: 'The bonding process can take anywhere from a few days to several weeks, depending on the size of the bond and your company\'s financial situation. We work to expedite the process as much as possible for Pennsylvania contractors.',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-green-400 text-sm font-semibold tracking-widest uppercase mb-4">Pennsylvania Contractor Resources</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Resources</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Helpful information and tools for construction professionals in Bucks County, Montgomery County, and Pennsylvania
          </p>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Knowledge Center</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {resources.map((resource, index) => (
              <div key={index} className="bg-slate-800 border border-slate-700 rounded-2xl p-8 hover:border-green-500/50 transition-all duration-300">
                <div className="w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${resource.icon} text-3xl text-green-400`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{resource.title}</h3>
                <p className="text-slate-400 mb-6">{resource.description}</p>
                <ul className="space-y-3">
                  {resource.items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-slate-300">
                      <i className="ri-arrow-right-s-line text-green-400 mt-1 mr-2 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Online Tools</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tools.map((tool, index) => (
              <a
                key={index}
                href={tool.link}
                target={tool.external ? '_blank' : undefined}
                rel={tool.external ? 'noopener noreferrer' : undefined}
                className="bg-slate-900 border border-slate-700 rounded-2xl p-8 hover:border-green-500/50 hover:shadow-xl hover:shadow-green-900/20 transition-all duration-300 hover:-translate-y-1 block cursor-pointer"
              >
                <div className="w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${tool.icon} text-3xl text-green-400`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{tool.title}</h3>
                <p className="text-slate-400 mb-4">{tool.description}</p>
                <span className="inline-flex items-center text-green-400 font-semibold">
                  Access Tool
                  <i className="ri-arrow-right-line ml-2"></i>
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Frequently Asked Questions</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto"></div>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-4 flex items-start">
                  <i className="ri-question-line text-green-400 mr-3 mt-1 flex-shrink-0"></i>
                  {faq.question}
                </h3>
                <p className="text-slate-300 leading-relaxed ml-9">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-700 to-green-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Need More Information?</h2>
          <p className="text-xl text-white/80 mb-10">
            Our team is here to answer your questions about construction insurance and surety bonding in Pennsylvania
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 bg-white text-green-700 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap"
          >
            Contact Our Team
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourcesPage;
